<template>
  <panel-layout
    id="voucher-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="voucher" class="flex items-center space-x-2">
        <div>
          <h2
            class="text-lg md:text-xl tracking-tight font-bold text-gray-800/85 leading-none dark:text-gray-100"
          >
            {{ voucher.isNewRecord ? $t('pages.promoCode.edit.new') : voucher.name }}
          </h2>
          <div v-if="!voucher.isNewRecord" class="mt-1">
            <p class="text-gray-700 text-sm md:text-base dark:text-gray-200" v-html="$t('pages.promoCode.edit.code', { code: voucher.code })" />
          </div>
        </div>
      </div>
    </template>

    <o-form v-if="voucher" ref="form" class="h-full w-full">
      <o-tabs ref="tabsRef" nav-class="bg-gray-50 h-11 border-b px-2 dark:bg-gray-900" scroll>
        <o-tab :name="$t('general.details')" icon="details">
          <voucher-details
            v-model:voucher="voucher"
            v-model:code-field="codeField"
            v-model:code-list="codeList"
            :isEditMode="isEditMode"
          />
        </o-tab>
        <o-tab
          :name="$t('general.marketing')"
          icon="marketing"
          :isVisible="!voucher.isNewRecord"
        >
          <voucher-marketing v-model:voucher="voucher" />
        </o-tab>
        <o-tab :name="$t('pages.promoCode.edit.validity')" icon="datepicker">
          <voucher-validity v-model:voucher="voucher" />
        </o-tab>
        <o-tab :name="$t('general.services')" icon="services">
          <voucher-services v-model:voucher="voucher" />
        </o-tab>
        <o-tab :name="$t('general.employees')" icon="employee">
          <voucher-employees v-model:voucher="voucher" />
        </o-tab>
      </o-tabs>
    </o-form>

    <template #footer>
      <div v-if="voucher">
        <div class="flex justify-between align-items">
          <div class="flex items-center space-x-1">
            <o-button outline flat class="mr-2" @click="cancel()">
              {{ $t('general.cancel') }}
            </o-button>
            <o-history-link
              v-if="!voucher.isNewRecord"
              :item="voucher"
              model="Voucher"
              outline
              flat
              class="mr-2"
            />
          </div>
          <div class="flex items-center space-x-1">
            <o-button
              v-if="codeField === 'single'"
              variant="primary"
              outline
              icon="save"
              @click="save()"
            >
              {{ $t('general.save') }}
            </o-button>

            <o-button
              variant="primary"
              icon="save"
              class="ml-2"
              :disabled="codeField === 'multiple' && codeList.length === 0"
              @click="save(true)"
            >
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="createDialog"
      :title="$t('pages.promoCode.edit.confirmCreation')"
      :info="$t('pages.promoCode.edit.creationInfo', { count: codeList.length })"
      :options="[
        { text: $t('general.cancel'), type: 'cancel' },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => saveBatch()
        }
      ]"
    />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        {
          text: $t('general.closeWithoutSaving'),
          type: 'confirm',
          variant: undefined,
          flat: true,
          outline: true,
          callback: () => cancel(true)
        },
        {
          text: $t('general.saveAndClose'),
          type: 'confirm',
          response: 'save',
          callback: () => save(true)
        }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
import { validate } from 'vee-validate'

import VoucherDetails from '@/components/voucher/edit/Details.vue'
import VoucherMarketing from '@/components/voucher/edit/Marketing.vue'
import VoucherValidity from '@/components/voucher/edit/Validity.vue'
import VoucherServices from '@/components/voucher/edit/Services.vue'
import VoucherEmployees from '@/components/voucher/edit/Employees.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { OTabs, OTab, OAlert } from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const props = defineProps<{ id?: string }>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const voucher = ref<any>(null)
const errors = ref(false)
const loading = ref(true)
const codeField = ref('single')
const codeList = ref<any[]>([])

const confirmDialog = ref<any>()
const createDialog = ref<any>()
const form = ref<any>()
const tabsRef = ref<any>()

const { unsavedChanges, snapshot } = useUnsavedChanges(voucher)

const isEditMode = computed(() => {
  return Boolean(voucher.value?.idVoucher)
})

const validateForm = async () => {
  const previousTab = tabsRef.value?.getActiveTab()?.hash

  tabsRef.value?.selectTabByIndex(0)
  await nextTick()

  if (!await form.value?.validate()) {
    return false
  }

  if (codeField.value === 'single') {
    const codeResult = await validate(voucher.value.code, 'required|alpha_num')
    if (!codeResult.valid) {
      return false
    }
  } else if (codeField.value === 'multiple') {
    if (!codeList.value || codeList.value.length === 0) {
      return false
    }
    // Validate each code in the list
    for (const code of codeList.value) {
      const codeResult = await validate(code, 'required|alpha_num')
      if (!codeResult.valid) {
        return false
      }
    }
  }

  if (previousTab) {
    tabsRef.value?.selectTab(previousTab)
  }

  return true
}

const save = async (close = false) => {
  // Validate the form before saving
  const isValid = await validateForm()

  if (!isValid) {
    $notification.error({
      errors: [$i18n.t('general.validationError')],
    })
    return
  }

  loading.value = true
  errors.value = false

  if (codeField.value === 'single') {
    try {
      const _voucher = (await $api.voucher.save(voucher.value)).data

      if (_voucher) {
        voucher.value = _voucher
        snapshot()

        $notification.toast({
          title: $i18n.t('pages.promoCode.edit.saveSuccess'),
          text: $i18n.t('pages.promoCode.edit.saveSuccessInfo'),
          variant: 'success',
        })

        $bus.emit.updated('Voucher', _voucher)

        if (close) {
          panel.close()
        }
      }
    } catch (e: any) {
      console.log(e.errors)
      $notification.error({ errors: e.errors })
    }
  } else if (codeField.value === 'multiple') {
    createDialog.value.open()
  }

  loading.value = false
}

const saveBatch = async () => {
  // Validate the form before batch saving
  const isValid = await validateForm()

  if (!isValid) {
    $notification.error({
      errors: [$i18n.t('general.validationError')],
    })
    createDialog.value.close()
    return
  }

  loading.value = true
  errors.value = false

  const voucherPayload = getVoucherPayload()

  try {
    const vouchers = await $api.voucher.batchCreate(voucherPayload)

    if (vouchers) {
      $notification.toast({
        title: $i18n.t('pages.promoCode.edit.saveBatchSuccess'),
        text: $i18n.t('pages.promoCode.edit.saveBatchSuccessInfo'),
        variant: 'success',
      })

      $bus.emit.updated('Voucher', vouchers)

      panel.close(true)
    }
  } catch (e: any) {
    $notification.error({ errors: e.errors })
  }

  createDialog.value.close()
  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const getVoucherPayload = () => {
  const newVouchers = codeList.value.map((code) => {
    const _voucher = voucher.value.copy()
    _voucher.code = code

    return _voucher
  })

  return newVouchers
}

async function init(id?: string) {
  loading.value = true
  try {
    if (!id) {
      voucher.value = (await $api.voucher.defaults({} as any)).data
    } else {
      voucher.value = (await $api.voucher.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
</script>
